/**
 * Check 3 — post type vs what this platform × placement actually accepts.
 *
 * Delegates entirely to `placementDisabledReason` (placement-resolution.ts
 * :130-138) over `PLACEMENT_MEDIA_KINDS` × `PUBLISHER_MEDIA_KINDS` — the same
 * function `evaluatePlatformReadiness` calls, so the chip can never claim a
 * combination the publisher would reject. Its reasons are already
 * user-facing sentence-case strings ("Reels require video", "LinkedIn doesn't
 * accept video posts yet", "Instagram publishes video as a Reel"), so they are
 * surfaced verbatim rather than paraphrased into a second wording.
 *
 * NO FIX, deliberately. Swapping media kind is not a one-click action; a
 * button that opened a file picker and called itself a fix would be a fake
 * remedy. Saying nothing beats lying about fixability.
 */
import { platformDisplayName } from '../../../../shared/posting-readiness.ts';
import type { PlacementRow } from '../../platform-matrix/placement-matrix.ts';
import {
  type MediaKind,
  placementDisabledReason,
} from '../../platform-matrix/placement-resolution.ts';
import { CHECK_LABEL, type QualityCheck } from '../types.ts';

const MEDIA_KIND_LABEL: Record<MediaKind, string> = {
  image: 'a single image',
  video: 'video',
  carousel: 'a carousel',
  text: 'text-only posts',
};

export function checkMediaKind(input: {
  platform: string;
  row: PlacementRow | undefined;
  mediaKind: MediaKind;
}): QualityCheck {
  const { platform, row, mediaKind } = input;
  const base = { id: 'media_kind' as const, label: CHECK_LABEL.media_kind };

  if (!row) {
    return {
      ...base,
      status: 'skipped',
      detail: `Mora has no placement rules for ${platformDisplayName(platform)} yet.`,
    };
  }

  const reason = placementDisabledReason(platform, row.placement, mediaKind);
  if (reason) {
    return { ...base, status: 'fail', detail: reason };
  }

  return {
    ...base,
    status: 'pass',
    detail: `${platformDisplayName(platform)} ${row.placement} accepts ${MEDIA_KIND_LABEL[mediaKind]}.`,
  };
}
